import {action, makeObservable, observable} from 'mobx'
import {injectable} from 'inversify'
import {subscribe} from 'dna-react-ioc'
import i18next from '@/i18n'

import {
    BankAccount,
    BankAccountVerificationResponse,
    BankVerificationSteps,
    BusinessStructure,
    DocumentInfo,
    SaveApplicationRequest
} from '@/stores/onboarding/models/onboarding'
import {
    AccountType,
    Application,
    BankAccountEditData,
    BankAccountFormValues,
    BankAccountSaveResult,
    BankAccountValidationResult,
    BankAccountVerificationResult
} from './models'
import {
    saveSelfOnboardingApplication,
    verifySelfOnboardingBankAccountBusiness,
    verifySelfOnboardingBankAccountPersonal
} from './services'
import {ONBOARDING_STORE_SYMBOLS} from './symbols'
import {onboardingMessage} from '@/pages/onboarding/utils'
import {OnboardingStore} from '@/stores/onboarding/OnboardingStore'

@injectable()
export class OnboardingBankAccountStore {
    isSubmitting = false
    currentStep: 'initial' | 'missMatch' | 'success' | 'doc_upload' | 'need_doc_upload' = 'initial'
    verificationResponse: BankAccountVerificationResponse | null = null
    uploadedDocuments: DocumentInfo[] = []
    private onboardingStore: OnboardingStore

    constructor() {
        this.onboardingStore = subscribe<OnboardingStore>(ONBOARDING_STORE_SYMBOLS.OnboardingStore)

        makeObservable(this, {
            isSubmitting: observable,
            currentStep: observable,
            verificationResponse: observable,
            uploadedDocuments: observable,
            setSubmitting: action,
            setCurrentStep: action,
            setVerificationResponse: action,
            setUploadedDocuments: action,
            resetVerificationState: action,
            resetStore: action,
            removeDocument: action,
            onAccountFieldChange: action,
            formatAccountNumber: action,
            initializeEditMode: action,
            validateAccountType: action,
            validateDuplicateAccountNumber: action,
            validateBeforeSave: action,
            verifyBankAccountWithHandling: action,
            saveBankAccountWithHandling: action,
            getBankAccountVerificationFields: action
        })
    }

    setSubmitting(loading: boolean): void {
        this.isSubmitting = loading
    }

    setCurrentStep(step: BankVerificationSteps): void {
        this.currentStep = step
    }

    setVerificationResponse(response: BankAccountVerificationResponse | null): void {
        this.verificationResponse = response
    }

    setUploadedDocuments(documents: DocumentInfo[]): void {
        this.uploadedDocuments = documents
        if (documents.length > 0) {
            this.currentStep = 'doc_upload'
        }
    }

    resetVerificationState(): void {
        this.currentStep = 'initial'
        this.verificationResponse = null
        this.uploadedDocuments = []
    }

    resetStore(): void {
        this.isSubmitting = false
        this.currentStep = 'initial'
        this.verificationResponse = null
        this.uploadedDocuments = []
    }

    removeDocument(documentIndex: number): void {
        this.uploadedDocuments = this.uploadedDocuments.filter(
            (_, index) => index !== documentIndex
        )
        if (this.uploadedDocuments.length === 0) {
            this.setCurrentStep('doc_upload')
        }
    }

    onAccountFieldChange(): void {
        if (this.currentStep !== 'initial') {
            this.resetVerificationState()
        }
    }

    formatAccountNumber(value: string): string {
        return value.replace(/\D/g, '').slice(0, 8)
    }

    async initializeEditMode(editId: string): Promise<BankAccountEditData | null> {
        const editData = this.getBankAccountEditData(editId)

        if (editData) {
            if (editData.verificationResponse) {
                this.setVerificationResponse(editData.verificationResponse)

                const isVerified =
                    editData.verificationResponse.creditSafeVerificationResults?.verified === true

                if (isVerified) {
                    this.setCurrentStep('success')
                } else {
                    if (editData.verificationResponse.uploadDocManually) {
                        this.setCurrentStep('need_doc_upload')
                    } else {
                        this.setCurrentStep('missMatch')
                    }
                }
            }

            if (editData.documents) {
                this.setUploadedDocuments(editData.documents)
            }
        } else {
            this.resetVerificationState()
        }

        return editData
    }

    async verifyBankAccountWithHandling(
        values: BankAccountFormValues,
        editingAccountId?: string
    ): Promise<{
        success: boolean
        validationPassed: boolean
        step: string
    }> {
        try {
            const result = await this.verifyBankAccount(values, editingAccountId)

            if (result.success) {
                onboardingMessage.success(
                    i18next.t('OnboardingBankAccountStore.bankAccountVerificationSuccessful')
                )
                this.setCurrentStep('success')
                return {
                    success: true,
                    validationPassed: true,
                    step: 'success'
                }
            } else if (result.isValidationError) {
                // Don't change the step for validation errors
                return {
                    success: false,
                    validationPassed: false,
                    step: this.currentStep
                }
            } else if (result.requiresDocuments) {
                return {
                    success: false,
                    validationPassed: false,
                    step: 'need_doc_upload'
                }
            } else {
                this.setCurrentStep('missMatch')
                return {
                    success: false,
                    validationPassed: false,
                    step: 'missMatch'
                }
            }
        } catch (error) {
            console.error('Bank account verification failed:', error)
            return {
                success: false,
                validationPassed: false,
                step: 'missMatch'
            }
        }
    }

    getBankAccountEditData(accountId: string): BankAccountEditData | null {
        const accounts =
            this.onboardingStore.rawApplicationData?.bankAccounts ||
            this.onboardingStore.bankDetails?.accounts ||
            []
        const account = accounts.find(
            (acc: BankAccount) => acc.id?.toString() === accountId || acc.id === Number(accountId)
        )
        if (!account) return null

        const verificationResponse = account.creditSafeVerificationResults
            ? {
                  bankAccountNumber: account.bankAccountNumber,
                  sortCode: account.sortCode,
                  accountName: account.accountName,
                  bankName: account.bankName,
                  bankAccountType: account.bankAccountType,
                  validationResults: account.validationResults,
                  creditSafeVerificationResults: account.creditSafeVerificationResults,
                  id: null,
                  openBankVerificationResult: account.openBankVerificationResult,
                  uploadDocManually: account.uploadDocManually,
                  openBankingRequestSent: account.openBankingRequestSent,
                  openBankingQuestionnaire: account.openBankingQuestionnaire,
                  sortCodeFoundInBase: account.sortCodeFoundInBase,
                  fromApi: account.fromApi
              }
            : undefined

        const allDocuments = this.onboardingStore.rawApplicationData?.documents || []
        const documents = allDocuments
            .filter((doc: any) => doc.type === 'bank-statement' || doc.type?.startsWith('bank-'))
            .map((doc: any) => ({
                type: doc.type || 'bank-statement',
                name: doc.name,
                path: doc.path
            }))

        const result: BankAccountEditData = {
            accountType:
                this.onboardingStore.companyDetails?.entityType === BusinessStructure.SOLE_TRADER
                    ? 'personal'
                    : 'business',
            accountNumber: account.bankAccountNumber,
            sortCode: account.sortCode,
            verificationResponse,
            documents: documents.length > 0 ? documents : undefined
        }
        return result
    }

    validateAccountType(accountType: AccountType): BankAccountValidationResult {
        const entityType = this.onboardingStore.companyDetails?.entityType

        if (
            (entityType === BusinessStructure.COMPANY || entityType === BusinessStructure.LLP) &&
            accountType === 'personal'
        ) {
            return {
                isValid: false,
                errorMessage: i18next.t(
                    'OnboardingBankAccountStore.personalAccountsNotAllowedForCompanies'
                )
            }
        }

        return {isValid: true}
    }

    validateDuplicateAccountNumber(
        accountNumber: string,
        editingAccountId?: string
    ): BankAccountValidationResult {
        const existingAccounts = this.onboardingStore.bankDetails?.accounts || []

        const duplicateAccount = existingAccounts.find((account) => {
            const isSameAccountNumber = account.bankAccountNumber === accountNumber
            const isDifferentAccount = editingAccountId
                ? account.id?.toString() !== editingAccountId
                : true
            return isSameAccountNumber && isDifferentAccount
        })

        if (duplicateAccount) {
            return {
                isValid: false,
                errorMessage: i18next.t('OnboardingBankAccountStore.accountNumberAlreadyAdded')
            }
        }

        return {isValid: true}
    }

    async verifyBankAccount(
        values: BankAccountFormValues,
        editingAccountId?: string
    ): Promise<BankAccountVerificationResult> {
        try {
            this.setSubmitting(true)

            if (!this.onboardingStore.processKey) {
                onboardingMessage.error(i18next.t('OnboardingBankAccountStore.processKeyRequired'))
                return {success: false}
            }

            const validation = this.validateAccountType(values.accountType)
            if (!validation.isValid) {
                onboardingMessage.error(validation.errorMessage!)
                return {success: false, isValidationError: true}
            }

            const duplicateValidation = this.validateDuplicateAccountNumber(
                values.accountNumber,
                editingAccountId
            )
            if (!duplicateValidation.isValid) {
                onboardingMessage.error(duplicateValidation.errorMessage!)
                return {success: false, isValidationError: true}
            }

            const isBusinessAccount = values.accountType === 'business'

            const verifyRequest = {
                businessStructure: this.onboardingStore.companyDetails?.entityType,
                companyNumber: this.onboardingStore.companyDetails?.companyNumber || '',
                companyName: this.onboardingStore.companyDetails?.companyName || '',
                bankAccountNumber: values.accountNumber,
                bankAccountType: values.accountType,
                bankAccountName: this.onboardingStore.companyDetails?.companyName || '',
                sortCode: values.sortCode,
                ...(!isBusinessAccount && this.onboardingStore.owners?.length > 0
                    ? {
                          businessOwner: {
                              firstName: this.onboardingStore.owners[0]?.firstName || '',
                              surname:
                                  this.onboardingStore.owners[0]?.lastName ||
                                  this.onboardingStore.owners[0]?.surname ||
                                  '',
                              dateOfBirth: this.onboardingStore.owners[0]?.dateOfBirth,
                              gender: this.onboardingStore.owners[0]?.gender,
                              residentialAddress: {
                                  addressLine1:
                                      this.onboardingStore.owners[0]?.address?.addressLine1 || '',
                                  addressLine2:
                                      this.onboardingStore.owners[0]?.address?.addressLine2 || '',
                                  townOrCity:
                                      this.onboardingStore.owners[0]?.address?.townOrCity || '',
                                  countyOrState: 'United Kingdom',
                                  postalCode:
                                      this.onboardingStore.owners[0]?.address?.postalCode || '',
                                  country: 'United Kingdom'
                              }
                          }
                      }
                    : {})
            }

            let verificationResult

            try {
                verificationResult = isBusinessAccount
                    ? await verifySelfOnboardingBankAccountBusiness(verifyRequest)
                    : await verifySelfOnboardingBankAccountPersonal(verifyRequest)
            } catch (apiError: any) {
                this.setCurrentStep('missMatch')
                return {success: false}
            }

            const isVerified = verificationResult.creditSafeVerificationResults?.verified === true

            if (isVerified) {
                this.setCurrentStep('success')
                this.setVerificationResponse(verificationResult)
                return {success: true, verificationResponse: verificationResult}
            } else {
                this.setCurrentStep('need_doc_upload')

                const updatedVerificationResult = {
                    ...verificationResult,
                    uploadDocManually: true
                }

                this.setVerificationResponse(updatedVerificationResult)
                return {
                    success: false,
                    requiresDocuments: true,
                    verificationResponse: updatedVerificationResult
                }
            }
        } catch (error: any) {
            onboardingMessage.error(
                error.message || i18next.t('OnboardingBankAccountStore.failedToVerifyBankAccount')
            )
            return {success: false}
        } finally {
            this.setSubmitting(false)
        }
    }

    validateBeforeSave(): {isValid: boolean; errorMessage?: string} {
        if (!this.verificationResponse) {
            return {
                isValid: false,
                errorMessage: i18next.t('OnboardingBankAccountStore.verifyBankAccountFirst')
            }
        }

        if (this.verificationResponse.uploadDocManually && this.uploadedDocuments.length === 0) {
            return {
                isValid: false,
                errorMessage: i18next.t(
                    'OnboardingBankAccountStore.uploadRequiredDocumentsBeforeSaving'
                )
            }
        }

        return {isValid: true}
    }

    async saveBankAccount(
        values: BankAccountFormValues,
        editingAccountId?: string
    ): Promise<BankAccountSaveResult> {
        try {
            this.setSubmitting(true)

            if (!this.onboardingStore.processKey) {
                onboardingMessage.error(i18next.t('OnboardingBankAccountStore.processKeyRequired'))
                return {success: false}
            }

            const validation = this.validateBeforeSave()
            if (!validation.isValid) {
                onboardingMessage.error(validation.errorMessage!)
                return {success: false}
            }

            const requiresDocuments = this.verificationResponse!.uploadDocManually
            const uploadDocManually = requiresDocuments && this.uploadedDocuments.length > 0

            await this.saveBankAccountToApplication(
                this.verificationResponse,
                values,
                editingAccountId,
                uploadDocManually,
                this.uploadedDocuments
            )
            await this.onboardingStore.reloadApplicationData('save bank account')

            this.onboardingStore.syncRawApplicationDataWithBankAccounts()

            this.onboardingStore.validationResponse =
                await this.onboardingStore.validateApplication()
            this.onboardingStore.updateStepCompletionStatus()

            return {success: true}
        } catch (error: any) {
            onboardingMessage.error(i18next.t('OnboardingBankAccountStore.failedToSaveBankAccount'))
            return {success: false}
        } finally {
            this.setSubmitting(false)
        }
    }

    async saveBankAccountWithHandling(
        values: BankAccountFormValues,
        editingAccountId?: string
    ): Promise<BankAccountSaveResult> {
        const result = await this.saveBankAccount(values, editingAccountId)

        if (result.success) {
            onboardingMessage.success(
                editingAccountId
                    ? i18next.t('OnboardingBankAccountStore.bankAccountUpdatedSuccessfully')
                    : i18next.t('OnboardingBankAccountStore.bankAccountSavedSuccessfully')
            )
        }

        return result
    }

    getBankAccountVerificationFields(
        accountType: string,
        accountNumber: string,
        sortCode: string
    ): Array<{label: string; value: string}> {
        return [
            {
                label: i18next.t('OnboardingBankAccountStore.accountType'),
                value: accountType || i18next.t('OnboardingBankAccountStore.personalAccountType')
            },
            {
                label: i18next.t('OnboardingBankAccountStore.bankAccountNumber'),
                value: accountNumber || ''
            },
            {
                label: i18next.t('OnboardingBankAccountStore.sortCode'),
                value: sortCode || ''
            }
        ]
    }

    private async saveBankAccountToApplication(
        verificationResult: BankAccountVerificationResponse,
        values: BankAccountFormValues,
        editingAccountId?: string,
        uploadDocManually = false,
        uploadedDocuments: DocumentInfo[] = []
    ): Promise<void> {
        const applicationData: Application = this.onboardingStore.rawApplicationData

        const hasUploadedDocuments = uploadedDocuments.length > 0

        const bankAccount = {
            // Spread all verification response data first
            ...verificationResult,
            // Then override with specific values
            bankAccountNumber: values.accountNumber,
            sortCode: values.sortCode,
            accountName: this.onboardingStore.companyDetails?.companyName || '',
            bankName: verificationResult.bankName || '',
            bankAccountType: values.accountType,
            id: editingAccountId || Date.now(),
            uploadDocManually: hasUploadedDocuments ? true : uploadDocManually,
            openBankingRequestSent: verificationResult.openBankingRequestSent || false,
            openBankingQuestionnaire: verificationResult.openBankingQuestionnaire || null,
            sortCodeFoundInBase: verificationResult.sortCodeFoundInBase || false,
            fromApi: verificationResult.fromApi || false,
            isVerified: hasUploadedDocuments
                ? true
                : verificationResult.creditSafeVerificationResults?.verified ||
                  verificationResult.verified ||
                  verificationResult.isVerified ||
                  false,
            isComplete: hasUploadedDocuments
                ? true
                : verificationResult.creditSafeVerificationResults?.verified ||
                  verificationResult.verified ||
                  verificationResult.isVerified ||
                  false,
            // Include any additional verification fields
            accountId: verificationResult.accountId,
            errors: verificationResult.errors,
            warnings: verificationResult.warnings,
            requiresManualVerification: verificationResult.requiresManualVerification,
            creditSafeResults: verificationResult.creditSafeResults,
            sortCodeValid: verificationResult.sortCodeValid,
            ...(hasUploadedDocuments && {documents: uploadedDocuments})
        }

        const existingAccounts = applicationData.bankAccounts || []
        let updatedAccounts

        if (editingAccountId) {
            updatedAccounts = existingAccounts.map((account: any) =>
                account.id?.toString() === editingAccountId ||
                account.id === Number(editingAccountId)
                    ? {...account, ...bankAccount}
                    : account
            )
        } else {
            updatedAccounts = [...existingAccounts, bankAccount]
        }

        const existingDocuments = applicationData.documents || []
        const updatedDocuments =
            uploadedDocuments.length > 0
                ? [...existingDocuments, ...uploadedDocuments]
                : existingDocuments

        const updatedApplicationData = {
            ...applicationData,
            bankAccounts: updatedAccounts,
            documents: updatedDocuments,
            isFastTrack: (applicationData as any).isFastTrack ?? false,
            isMsaSigned: (applicationData as any).isMsaSigned ?? false,
            productVersion: (applicationData as any).productVersion ?? '2.1'
        }

        await saveSelfOnboardingApplication(
            this.onboardingStore.processKey!,
            updatedApplicationData as unknown as SaveApplicationRequest
        )
    }
}
